import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react';

/**
 * The small set of primitives the whole interface is built from.
 *
 * Deliberately thin: semantic HTML plus a class. A component library would add a dependency, a
 * bundle and a set of accessibility behaviours to audit, for a handful of screens that a `<button>`
 * already does correctly.
 */

export type ButtonVariant = 'primary' | 'secondary' | 'quiet' | 'danger';

export function Button({
  variant = 'secondary',
  className,
  ref,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  readonly variant?: ButtonVariant;
  /** React 19 passes ref as an ordinary prop; dialogs use it to place the initial focus. */
  readonly ref?: Ref<HTMLButtonElement>;
}) {
  return (
    <button
      type="button"
      ref={ref}
      className={['button', `button--${variant}`, className].filter(Boolean).join(' ')}
      {...props}
    />
  );
}

/** Visible to screen readers, not to the eye. For labels a sighted user gets from layout. */
export function VisuallyHidden({ children }: { readonly children: ReactNode }) {
  return <span className="visually-hidden">{children}</span>;
}

/**
 * Announces changes that happen away from the keyboard focus.
 *
 * `polite` waits for a pause in speech, which is right for a timer or a saved-confirmation;
 * `assertive` interrupts, and is reserved for something the user must act on.
 */
export function LiveRegion({
  children,
  assertive = false,
}: {
  readonly children: ReactNode;
  readonly assertive?: boolean;
}) {
  return (
    <div
      className="visually-hidden"
      role="status"
      aria-live={assertive ? 'assertive' : 'polite'}
      aria-atomic="true"
    >
      {children}
    </div>
  );
}

/**
 * Text that came from an imported file.
 *
 * The single rendering path for untrusted content, and the reason there is no
 * `dangerouslySetInnerHTML` anywhere in the project: React escapes children, `white-space:
 * pre-wrap` preserves the line breaks the author wrote, and `lang` tells a screen reader which
 * language to pronounce — the exam's, which is not necessarily the interface's.
 */
export function ExamText({
  children,
  lang,
  as: Element = 'p',
  className,
}: {
  readonly children: string;
  readonly lang: string;
  readonly as?: 'p' | 'div' | 'span' | 'h2' | 'h3';
  readonly className?: string;
}) {
  return (
    <Element lang={lang} className={['exam-text', className].filter(Boolean).join(' ')}>
      {children}
    </Element>
  );
}

export function Callout({
  tone = 'info',
  title,
  children,
}: {
  readonly tone?: 'info' | 'warning' | 'error' | 'success';
  readonly title?: string;
  readonly children: ReactNode;
}) {
  return (
    <div className={`callout callout--${tone}`} role={tone === 'error' ? 'alert' : undefined}>
      {title !== undefined && <p className="callout__title">{title}</p>}
      {children}
    </div>
  );
}

export function Spinner({ label }: { readonly label: string }) {
  return (
    <p className="spinner" role="status">
      <span className="spinner__dot" aria-hidden="true" />
      {label}
    </p>
  );
}
